import { useState } from "react";
import { Box } from "@mui/system";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import { useNavigate } from "react-router";
import { alpha } from '@mui/material/styles';
import EditIcon from "@mui/icons-material/Edit";
import { IconCalendarPlus } from "@tabler/icons";
import PatientSummary from "./../Forms/PatientSummary";
import { configState } from "../../../../recoil/atoms";
import { PatientApi } from "../../../../api/medical-operations";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, Typography, Grid, Tooltip } from "@mui/material";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import { IconAlertTriangle, IconArrowBack, IconCheck } from "@tabler/icons";
import useAuthorization from "../../../Authentication/permissions/Authorization";

function DuplicateInfoPatient(props: any) {
  const navigate = useNavigate();  
  const config = useRecoilValue(configState);
  const { patients, handleBack, message, valuesPatient } = props;  

  const [id, setId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [rowID, setRowID] = useState<string>("");
  const [patientAdded, setPatientAdded] = useState(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const { canEditTenantPatient, canDeleteTenantPatient, canScheduleAppointment } = useAuthorization();

  const addPatient = () => {
    setLoading(true);
    valuesPatient.AllowDuplicate = true

    const Patient = new PatientApi(config);
    Patient.addPatientAndUser(valuesPatient)
      .then((res) => {        
        setPatientAdded(true);
        toast.success(res?.data?.Message!);                     
        setId(res.data.Patients[0].PatientID || ""); 
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deletePatient = (id: string) => {
    const Patient = new PatientApi(config);
    Patient.deleteTenantPatient(id)
      .then((res) => {              
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };  

  const handleGoBack = () => {
    handleBack();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatient(rowID);
    handleCloseDialog();
  };  

  const columns = [
    {
      name: "PatientID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },                   
    {
      name: "FirstName",
      label: "First Name",
      options: {
        filter: true,
      },
    },
    {
      name: "MiddleName",
      label: "Middle Name",
      options: {
        filter: true,
      },
    },
    {
      name: "FamilyName",
      label: "Family Name",
      options: {
        filter: true,
      },
    },   
    {
      name: "Gender",
      label: "Gender",
      options: {
        filter: true,
      },
    },    
    {
      name: "PrimaryPhone",
      label: "Primary Phone",
      options: {
        filter: true,
      },
    },
    {
      name: "SecondaryPhone",
      label: "Secondary Phone",
      options: {
        filter: true,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canEditTenantPatient && (
              <Tooltip title="Edit Patient">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {                                       
                    navigate(`/patient/${tableMeta.rowData[0]}/user`, {
                      state: tableMeta.tableData[tableMeta.rowIndex],                                            
                    });                                                                                                                                                        
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>                
              </Tooltip>
            )}
            {canDeleteTenantPatient && (
              <Tooltip title="Delete Patient">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    handleDelete(tableMeta.rowData[0]);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}            
            {canScheduleAppointment && (
              <Tooltip title="Add Appointment">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                    height: "40px",
                    borderColor: alpha('#8e44ad', 0.7),
                    '&:hover': {
                      borderColor: '#8e44ad',
                    },
                  }}
                  onClick={() => {
                    navigate(`/add-appointment/${tableMeta.rowData[0]}`, {
                      state: tableMeta.tableData[tableMeta.rowIndex]
                    });
                  }}                  
                >
                  <IconCalendarPlus color="purple" />
                </Button>
              </Tooltip>
            )}            
          </>
        ),
      },
    },
  ];

  return (
    <>
      {!patientAdded ? (
        <>
          <Grid>
            <Box sx={{ p: 8, width: "100%", textAlign: "center" }}>
              <Box sx={{ pb: 1 }}>
                <IconAlertTriangle size={100} color="red"/>
              </Box>
              <Typography variant="h5" gutterBottom color={"red"}>
                {message} 
              </Typography>
              <Typography variant="body1" color="secondary" sx={{ mt: 4 }}>
                You can continue adding the Patient or go back and change the information
              </Typography>    
              <Box style={{ display: "flex", flexDirection: "row", justifyContent: "space-around" }} sx={{ mt: 4 }}>                           
                <Button color="secondary" onClick={handleGoBack}>
                  <IconArrowBack style={{ marginRight: 6}}/> Back
                </Button>
                <Button 
                  color="secondary" 
                  onClick={addPatient} 
                  disabled={loading}
                >
                  <IconCheck style={{ marginRight: 6}}/> {loading ? "Submitting..." : "Add Patient Anyway"}
                </Button>                
              </Box>    
            </Box>
          </Grid>
          <MUIDataTable
            data={patients}  
            columns={columns}
            options={{
              filter: true,
              viewColumns: false,
              print: false,
              download: false,
              selectableRows: "none",
              selectableRowsOnClick: false,
              elevation: 0,
              rowsPerPage: 10,
              filterType: "dropdown",
              enableNestedDataAccess: ".",
              responsive: "standard",          
            }}
            title=""
          />
          <ConfirmDialog
            name={"patient"}
            handleCloseDialog={handleCloseDialog}
            handleConfirm={handleConfirm}
            openDialog={openDialog}
          />
        </>  
      ): (
        <PatientSummary id={id}/>
      )}
    </>
  );
}

export default DuplicateInfoPatient;
